import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CopyableValueProps {
  value?: string | number;
  sx?: SxProps<Theme>;
}

const COPY_ICON_CLASS = 'copy-icon';

const copyableSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-block',
  maxWidth: '100%',
  color: 'text.primary',
  cursor: 'pointer',
  transition: 'color 0.15s ease',
  '&:hover': { color: 'primary.main' },
  [`&:hover .${COPY_ICON_CLASS}`]: { opacity: 0.7 },
};

const copyIconSx: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  left: 'calc(100% + 6px)',
  transform: 'translateY(-50%)',
  fontSize: 14,
  opacity: 0,
  transition: 'opacity 0.15s ease',
  pointerEvents: 'none',
};

const CopyableValue = ({ value, sx }: CopyableValueProps): JSX.Element => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (value == null) return;
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [value]);

  return (
    <Tooltip
      title={copied ? t('common.copied') : t('common.clickToCopy')}
      arrow
    >
      <Box
        component="span"
        onClick={handleCopy}
        sx={[copyableSx, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {value}
        <ContentCopyIcon className={COPY_ICON_CLASS} sx={copyIconSx} />
      </Box>
    </Tooltip>
  );
};

export default CopyableValue;
